<template>
  <header class="MainHeader h-header">
    <h1 v-if="!userIsLogged" class="Title is-not-logged">{{ sectionTitle }}</h1>
    <template v-else>
      <transition name="fade" mode="out-in">
        <!-- Landscape -->
        <div v-if="isLandscape && !isLandscapeSettings" class="LandscapeHeader">
          <router-link
            class="Title"
            :to="{params: {actor: null}, query: $route.query}"
          >
            {{ landscapeName }}
          </router-link>
          <landscape-nav />
          <landscape-options />
        </div>
        <div
          v-else-if="isLandscape && isLandscapeSettings"
          class="w-full flex items-center pl-3 desktop:pl-5"
        >
          <lm-icon-button
            icon="arrowDown"
            svg
            bg-black
            tooltip="Back to Landscape"
            class="transform rotate-90"
            @click="backToLandscape"
          />
          <h1
            class="flex-grow text-center text-white text-s uppercase font-mono whitespace-nowrap"
          >
            <span class="hidden desktop:inline text-gray-700 truncate"
              >{{ landscapeName }} /&nbsp;</span
            >
            <span>Settings</span>
          </h1>
        </div>
        <!-- Pages (Account Settings, etc) or Error -->
        <h1 v-else class="Title PageTitle">{{ sectionTitle }}</h1>
      </transition>
      <lm-icon-button
        class="Hamburger h-header"
        :class="{'is-new': notifications}"
        icon="menu"
        @click="openSidenav"
      />
    </template>
  </header>
</template>

<script>
import LandscapeNav from '@/components/common/header/LandscapeNav'
import {mapGetters} from 'vuex'
import {Events} from '@/lib/definitions'
import EventHub from '@/lib/eventHub'
import LandscapeOptions from './header/LandscapeOptions'
import LmIconButton from '@/components/ui/LmIconButton.vue'
import {TimelineLite} from 'gsap'

export default {
  name: 'MainHeader',
  components: {
    LandscapeNav,
    LmIconButton,
    LandscapeOptions
  },
  data() {
    return {
      landscapeName: '',
      routeFrom: null
    }
  },
  computed: {
    ...mapGetters([
      'userIsLogged',
      'sectionTitle',
      'currentLandscape',
      'landscapes',
      'invitedLandscapes',
      'newNews'
    ]),
    isLandscape() {
      return this.$route.meta.landscape
    },
    isLandscapeSettings() {
      return this.$route.meta.settingsSection
    },
    notifications() {
      if (!this.currentLandscape) return false
      return (
        this.invitedLandscapes.length > 0 ||
        this.newNews ||
        this.landscapes.some(
          ld => ld._id !== this.currentLandscape._id && ld.mentions > 0
        )
      )
    }
  },
  watch: {
    $route(to, from) {
      const settingsRoute = '/landscape/:landscapeSlug/settings'
      const isSettings = from.matched[1]?.path === settingsRoute
      if (from.name && !isSettings) this.routeFrom = from
    },
    sectionTitle(newValue) {
      if (this.isLandscape) {
        const title = document.querySelector('.LandscapeHeader .Title')
        if (title) {
          const tl = new TimelineLite()
          tl.to(title, 0.3, {opacity: 0})
          tl.add(() => {
            this.landscapeName = newValue
          })
          tl.to(title, 0.6, {opacity: 1})
        } else {
          this.landscapeName = newValue
        }
      }
    }
  },
  mounted() {
    if (this.isLandscape) {
      this.landscapeName = this.sectionTitle
    }
  },
  methods: {
    openSidenav() {
      EventHub.emit(Events.OPEN_SIDENAV)
    },
    backToLandscape() {
      this.$router.push(this.routeFrom || {name: 'info'})
    }
  }
}
</script>

<style scoped>
.MainHeader {
  --hamburger-width: 60px;
  --hamburger-width-desktop: 72px;
  position: relative;
  display: flex;
  align-items: center;
  z-index: theme('zIndex.mainHeader');
  background-color: theme('colors.gray.900');
}
.Title {
  width: calc(100% - var(--hamburger-width));
  margin: 0;
  font-family: theme('fontFamily.mono');
  font-size: 14px;
  text-align: center;
  text-transform: uppercase;
  color: white;
  &.PageTitle {
    padding-left: var(--hamburger-width); /* Hamburger offset */
  }
  &.is-not-logged {
    width: 100%;
  }
}
.Hamburger {
  position: relative;
  width: calc(var(--hamburger-width) + 1px);
  border-left: 1px solid theme('colors.gray.700');
  color: theme('colors.gray.700');
  &:hover {
    color: white;
  }
  &.is-new:before {
    content: '';
    position: absolute;
    top: 12px;
    left: 14px;
    width: 15px;
    height: 15px;
    background-color: theme('colors.highlight');
    border: 4px solid theme('colors.gray.900');
    border-radius: 100%;
    animation: blink 0.6s 4;
  }
}
.LandscapeHeader {
  width: calc(100% - var(--hamburger-width));
  display: flex;
  justify-content: space-between;
  align-items: center;
  .Title {
    width: auto;
    padding: 0 20px;
    @apply truncate;
    &:hover {
      color: theme('colors.gray.500');
      text-decoration: none;
    }
  }
}
@media (max-width: 919px) {
  .LandscapeHeader .LandscapeNav {
    display: none;
  }
}
@media (--desktop) {
  .MainHeader .Hamburger {
    width: calc(var(--hamburger-width-desktop) + 1px);
    &.is-new:before {
      top: 20px;
      left: 19px;
    }
  }
  .LandscapeHeader {
    width: calc(100% - var(--hamburger-width-desktop));
    .Title {
      padding: 0 24px;
    }
  }
}
</style>
